import React, { useState, useEffect } from 'react';
import { 
  Filter, RefreshCw, Bookmark, Share2, Eye, Clock, TrendingUp, 
  ChevronRight, Play, FileText, Headphones, Video, ExternalLink,
  Heart, MessageCircle, Star, Award, Globe, Calendar, User,
  Search, ChevronDown, Zap, Brain, Target, BarChart3
} from 'lucide-react';

const LearningNews = () => {
  const [darkMode, setDarkMode] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // grid, list, magazine
  const [showPreview, setShowPreview] = useState(null);
  const [bookmarkedItems, setBookmarkedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('trending');

  const categories = [
    { id: 'all', name: 'All', icon: Globe, color: 'blue' },
    { id: 'ai-ml', name: 'AI & ML', icon: Brain, color: 'purple' },
    { id: 'programming', name: 'Programming', icon: FileText, color: 'green' },
    { id: 'science', name: 'Science', icon: Zap, color: 'yellow' },
    { id: 'business', name: 'Business', icon: Target, color: 'red' },
    { id: 'design', name: 'Design', icon: Star, color: 'pink' },
    { id: 'data', name: 'Data Science', icon: BarChart3, color: 'indigo' }
  ];

  const learningNews = [
    {
      id: 1,
      title: "Revolutionary Breakthrough in Quantum Machine Learning Algorithms",
      summary: "Scientists develop new quantum computing approach that could accelerate AI training by 1000x",
      category: 'ai-ml',
      source: 'Nature AI',
      author: 'Dr. Sarah Chen',
      authorAvatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face',
      date: new Date('2025-06-15'),
      readTime: '8 min',
      difficulty: 'Advanced',
      views: 15420,
      likes: 892,
      comments: 156,
      trending: true,
      featured: true,
      contentType: 'article',
      image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400&h=250&fit=crop',
      tags: ['Quantum Computing', 'Machine Learning', 'Research'],
      content: `<p>A groundbreaking study published today reveals a new approach to quantum machine learning that could revolutionize how we train artificial intelligence systems...</p>`,
      learningOutcomes: ['Understand quantum ML principles', 'Apply new algorithms', 'Evaluate performance gains'],
      prerequisites: ['Linear Algebra', 'Basic Quantum Physics', 'Machine Learning Fundamentals'],
      relatedCourses: ['Quantum Computing 101', 'Advanced ML Algorithms'],
      estimatedLearningTime: '2-3 hours',
      practicalApplications: ['Drug Discovery', 'Financial Modeling', 'Climate Simulation']
    },
    {
      id: 2,
      title: "The Future of Web Development: WebAssembly and Edge Computing",
      summary: "How WebAssembly is reshaping frontend development and enabling near-native performance in browsers",
      category: 'programming',
      source: 'Dev Insider',
      author: 'Alex Rodriguez',
      authorAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face',
      date: new Date('2025-06-14'),
      readTime: '12 min',
      difficulty: 'Intermediate',
      views: 8965,
      likes: 432,
      comments: 89,
      trending: false,
      featured: false,
      contentType: 'tutorial',
      image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=400&h=250&fit=crop',
      tags: ['WebAssembly', 'Edge Computing', 'Performance'],
      content: `<p>WebAssembly (WASM) is transforming how we think about web performance and capabilities...</p>`,
      learningOutcomes: ['Master WebAssembly basics', 'Implement edge computing solutions', 'Optimize web performance'],
      prerequisites: ['JavaScript', 'Basic Computer Science', 'Web Development'],
      relatedCourses: ['Modern JavaScript', 'System Programming'],
      estimatedLearningTime: '4-5 hours',
      practicalApplications: ['High-Performance Web Apps', 'Gaming', 'Scientific Computing']
    },
    {
      id: 3,
      title: "Breakthrough in CRISPR Gene Editing: Treating Genetic Blindness",
      summary: "Clinical trials show 90% success rate in restoring vision using advanced gene therapy techniques",
      category: 'science',
      source: 'Science Today',
      author: 'Dr. Maria Gonzalez',
      authorAvatar: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=40&h=40&fit=crop&crop=face',
      date: new Date('2025-06-13'),
      readTime: '6 min',
      difficulty: 'Beginner',
      views: 23150,
      likes: 1205,
      comments: 234,
      trending: true,
      featured: true,
      contentType: 'research',
      image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop',
      tags: ['CRISPR', 'Gene Therapy', 'Medical Breakthrough'],
      content: `<p>A revolutionary gene therapy treatment using CRISPR technology has shown unprecedented success...</p>`,
      learningOutcomes: ['Understand CRISPR mechanisms', 'Learn gene therapy applications', 'Analyze clinical trial data'],
      prerequisites: ['Basic Biology', 'Genetics Fundamentals'],
      relatedCourses: ['Molecular Biology', 'Bioethics'],
      estimatedLearningTime: '2-3 hours',
      practicalApplications: ['Medical Treatment', 'Research', 'Biotech Industry']
    }
  ];

  const toggleBookmark = (id) => {
    setBookmarkedItems(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const getFilteredNews = () => {
    let filtered = learningNews;
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }
    
    if (searchQuery) {
      filtered = filtered.filter(item => 
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    // Sort logic
    if (sortBy === 'trending') {
      filtered = [...filtered].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'recent') {
      filtered = [...filtered].sort((a, b) => new Date(b.date) - new Date(a.date));
    } else if (sortBy === 'popular') {
      filtered = [...filtered].sort((a, b) => b.likes - a.likes);
    }
    
    return filtered;
  };

  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case 'Beginner': return 'bg-green-500/20 text-green-300';
      case 'Intermediate': return 'bg-yellow-500/20 text-yellow-300';
      case 'Advanced': return 'bg-red-500/20 text-red-300';
      default: return 'bg-gray-500/20 text-gray-300';
    }
  };

  const getCategoryIcon = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.icon : Globe;
  };

  const getCategoryColor = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    const colors = {
      blue: 'bg-blue-500/20 text-blue-300',
      purple: 'bg-purple-500/20 text-purple-300',
      green: 'bg-green-500/20 text-green-300',
      yellow: 'bg-yellow-500/20 text-yellow-300',
      red: 'bg-red-500/20 text-red-300',
      pink: 'bg-pink-500/20 text-pink-300',
      indigo: 'bg-indigo-500/20 text-indigo-300'
    };
    return colors[category?.color] || 'bg-gray-500/20 text-gray-300';
  };

  const NewsPreviewModal = ({ newsItem, onClose }) => {
    if (!newsItem) return null;

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className={`max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-3xl ${darkMode ? 'bg-gray-900' : 'bg-white'} shadow-2xl`}>
          <div className="relative">
            <img 
              src={newsItem.image} 
              alt={newsItem.title}
              className="w-full h-64 object-cover rounded-t-3xl"
            />
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm text-white p-2 rounded-full hover:bg-black/70 transition-colors"
            >
              ×
            </button>
            {newsItem.featured && (
              <div className="absolute top-4 left-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                <Award className="w-4 h-4 inline mr-1" />
                Featured
              </div>
            )}
          </div>
          
          <div className="p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <img 
                  src={newsItem.authorAvatar} 
                  alt={newsItem.author}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-medium">{newsItem.author}</p>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{newsItem.source}</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <span className={`px-3 py-1 rounded-full text-sm ${getDifficultyColor(newsItem.difficulty)}`}>
                  {newsItem.difficulty}
                </span>
                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {newsItem.readTime} read
                </span>
              </div>
            </div>

            <h1 className="text-3xl font-bold mb-4">{newsItem.title}</h1>
            <p className={`text-lg mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {newsItem.summary}
            </p>

            <div className="flex flex-wrap gap-2 mb-6">
              {newsItem.tags.map((tag, index) => (
                <span key={index} className={`px-3 py-1 rounded-full text-sm ${getCategoryColor(newsItem.category)}`}>
                  #{tag}
                </span>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className={`p-4 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <h3 className="font-semibold mb-3 flex items-center">
                  <Target className="w-5 h-5 mr-2 text-blue-400" />
                  Learning Outcomes
                </h3>
                <ul className="space-y-2">
                  {newsItem.learningOutcomes.map((outcome, index) => (
                    <li key={index} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      • {outcome}
                    </li>
                  ))}
                </ul>
              </div>

              <div className={`p-4 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <h3 className="font-semibold mb-3 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-green-400" />
                  Prerequisites
                </h3>
                <ul className="space-y-2">
                  {newsItem.prerequisites.map((prereq, index) => (
                    <li key={index} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      • {prereq}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-gray-50'} mb-6`}>
              <h3 className="font-semibold mb-4 flex items-center">
                <Zap className="w-5 h-5 mr-2 text-yellow-400" />
                Practical Applications
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {newsItem.practicalApplications.map((app, index) => (
                  <div key={index} className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} text-center`}>
                    <span className="text-sm font-medium">{app}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-6 border-t border-gray-200">
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => toggleBookmark(newsItem.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-colors ${
                    bookmarkedItems.includes(newsItem.id) 
                      ? 'bg-blue-500 text-white' 
                      : `${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`
                  }`}
                >
                  <Bookmark className="w-4 h-4" />
                  <span>{bookmarkedItems.includes(newsItem.id) ? 'Saved' : 'Save'}</span>
                </button>
                
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span className="flex items-center">
                    <Eye className="w-4 h-4 mr-1" />
                    {newsItem.views.toLocaleString()}
                  </span>
                  <span className="flex items-center">
                    <Heart className="w-4 h-4 mr-1" />
                    {newsItem.likes.toLocaleString()}
                  </span>
                  <span className="flex items-center">
                    <MessageCircle className="w-4 h-4 mr-1" />
                    {newsItem.comments}
                  </span>
                </div>
              </div>
              
              <button className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-full hover:from-blue-600 hover:to-purple-700 transition-all duration-300 flex items-center">
                <ExternalLink className="w-4 h-4 mr-2" />
                Read Full Article
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                Learning Intelligence Hub
              </h1>
              <p className={`text-lg mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Discover cutting-edge insights and breakthrough knowledge across disciplines
              </p>
            </div>
            <button 
              onClick={() => setDarkMode(!darkMode)}
              className={`p-3 rounded-full ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'} shadow-lg transition-colors`}
            >
              {darkMode ? '☀️' : '🌙'}
            </button>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search breakthrough insights, research, tutorials..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-12 pr-4 py-3 rounded-2xl ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
              />
            </div>
            
            <div className="flex gap-3">
              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={`px-4 py-3 rounded-2xl ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border focus:ring-2 focus:ring-blue-500`}
              >
                <option value="trending">🔥 Trending</option>
                <option value="recent">📅 Recent</option>
                <option value="popular">❤️ Popular</option>
              </select>
              
              <button 
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className={`px-4 py-3 rounded-2xl ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'} border transition-colors`}
              >
                {viewMode === 'grid' ? '📋' : '📱'}
              </button>
            </div>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-3 mb-6">
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-all duration-300 ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg transform scale-105'
                      : `${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'} shadow-md hover:shadow-lg`
                  }`}
                >
                  <IconComponent className="w-4 h-4" />
                  <span className="font-medium">{category.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* News Grid */}
        <div className={`grid gap-6 ${viewMode === 'grid' ? 'lg:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>
          {getFilteredNews().map((newsItem) => {
            const CategoryIcon = getCategoryIcon(newsItem.category);
            return (
              <div 
                key={newsItem.id} 
                className={`group relative rounded-3xl overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2`}
              >
                <div className="relative overflow-hidden">
                  <img 
                    src={newsItem.image} 
                    alt={newsItem.title}
                    className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  
                  {/* Overlay badges */}
                  <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                    {newsItem.featured && (
                      <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                        <Award className="w-3 h-3 inline mr-1" />
                        Featured
                      </span>
                    )}
                    {newsItem.trending && (
                      <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-medium animate-pulse">
                        <TrendingUp className="w-3 h-3 inline mr-1" />
                        Trending
                      </span>
                    )}
                  </div>

                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center space-x-3 mb-2">
                      <img 
                        src={newsItem.authorAvatar} 
                        alt={newsItem.author}
                        className="w-8 h-8 rounded-full border-2 border-white/50"
                      />
                      <div>
                        <p className="text-white text-sm font-medium">{newsItem.author}</p>
                        <p className="text-white/80 text-xs">{newsItem.source}</p>
                      </div>
                    </div>
                  </div>

                  <button 
                    onClick={() => setShowPreview(newsItem)}
                    className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm text-white p-2 rounded-full hover:bg-white/30 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className={`flex items-center px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(newsItem.category)}`}>
                        <CategoryIcon className="w-3 h-3 mr-1" />
                        {categories.find(cat => cat.id === newsItem.category)?.name}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs ${getDifficultyColor(newsItem.difficulty)}`}>
                        {newsItem.difficulty}
                      </span>
                    </div>
                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {newsItem.readTime}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold mb-3 line-clamp-2 group-hover:text-blue-400 transition-colors">
                    {newsItem.title}
                  </h3>
                  
                  <p className={`text-sm mb-4 line-clamp-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {newsItem.summary}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span className="flex items-center">
                        <Eye className="w-3 h-3 mr-1" />
                        {newsItem.views.toLocaleString()}
                      </span>
                      <span className="flex items-center">
                        <Heart className="w-3 h-3 mr-1" />
                        {newsItem.likes.toLocaleString()}
                      </span>
                      <span className="flex items-center">
                        <MessageCircle className="w-3 h-3 mr-1" />
                        {newsItem.comments}
                      </span>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => toggleBookmark(newsItem.id)}
                        className={`p-2 rounded-full transition-colors ${
                          bookmarkedItems.includes(newsItem.id) 
                            ? 'text-blue-500' 
                            : `${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`
                        }`}
                      >
                        <Bookmark className="w-4 h-4" />
                      </button>
                      <button className={`p-2 rounded-full transition-colors ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>
                        <Share2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <button 
                    onClick={() => setShowPreview(newsItem)}
                    className="w-full mt-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-2xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 flex items-center justify-center space-x-2 group"
                  >
                    <span>Explore Learning Path</span>
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Preview Modal */}
        {showPreview && (
          <NewsPreviewModal 
            newsItem={showPreview} 
            onClose={() => setShowPreview(null)} 
          />
        )}
      </div>
    </div>
  );
};

export default LearningNews;